import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
    RefreshCw,
    Copy,
    Check,
    Terminal,
    Clock,
    AlertTriangle,
    FileJson,
    ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDebugPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [debugData, setDebugData] = useState(null);
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState(null);
    const [systemMode, setSystemMode] = useState('loading');

    // Fetch settings to get system mode
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await fetch(`${API_URL}/api/settings`, {
                    credentials: 'include'
                });
                if (response.ok) {
                    const data = await response.json();
                    setSystemMode(data.system_mode || 'production');
                } else {
                    setSystemMode('unknown');
                }
            } catch (err) {
                console.error('Failed to fetch settings:', err);
                setSystemMode('error');
            }
        };
        fetchSettings();
    }, []);

    const fetchDebugData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_URL}/api/debug/last-request`, {
                credentials: 'include'
            });
            if (response.ok) {
                const data = await response.json();
                setDebugData(data);
            } else if (response.status === 403) {
                setError('Access denied by backend. Check role and mode.');
            } else {
                setError(`Failed: ${response.status} ${response.statusText}`);
            }
        } catch (err) {
            setError(`Network error: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const handleCopyReport = async () => {
        try {
            const response = await fetch(`${API_URL}/api/debug/report`, {
                credentials: 'include'
            });
            if (response.ok) {
                const data = await response.json();
                await navigator.clipboard.writeText(data.report);
                setCopied(true);
                toast.success('Debug report copied to clipboard');
                setTimeout(() => setCopied(false), 2000);
            } else {
                toast.error('Failed to generate debug report');
            }
        } catch (err) {
            toast.error(`Failed to copy: ${err.message}`);
        }
    };

    const formatJson = (data) => {
        if (data === null || data === undefined) return 'null';
        try {
            return JSON.stringify(data, null, 2);
        } catch {
            return String(data);
        }
    };

    const getStatusColor = (statusCode) => {
        if (!statusCode) return 'bg-zinc-600';
        if (statusCode >= 500) return 'bg-red-600';
        if (statusCode >= 400) return 'bg-orange-600';
        if (statusCode >= 200) return 'bg-green-600';
        return 'bg-zinc-600';
    };

    // Determine access
    const isAdmin = user?.role === 'admin';
    const isTesting = systemMode === 'testing';
    const hasAccess = isAdmin && isTesting;

    const lastRequest = debugData?.last_request;
    const recentLogs = debugData?.recent_logs || [];

    // ALWAYS render - no early returns
    return (
        <div style={{ minHeight: '100vh', backgroundColor: '#09090b', color: '#fff' }}>
            {/* Always visible header */}
            <div style={{ 
                backgroundColor: '#18181b', 
                borderBottom: '1px solid #27272a',
                padding: '16px 24px'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <button 
                            onClick={() => navigate('/settings')}
                            style={{ 
                                background: 'none', 
                                border: 'none', 
                                color: '#a1a1aa', 
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            <ArrowLeft size={16} />
                            Settings
                        </button>
                        <div style={{ width: '1px', height: '24px', backgroundColor: '#3f3f46' }} />
                        <Terminal size={24} style={{ color: '#ef4444' }} />
                        <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>Debug Console</h1>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <Button
                            variant="outline"
                            onClick={fetchDebugData}
                            disabled={isLoading || !hasAccess}
                            className="border-zinc-700"
                        >
                            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                            Refresh
                        </Button>
                        <Button
                            onClick={handleCopyReport}
                            disabled={!hasAccess}
                            className="bg-orange-600 hover:bg-orange-700"
                        >
                            {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                            {copied ? 'Copied!' : 'Copy Report'}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Status bar - always visible */}
            <div style={{ 
                backgroundColor: '#1c1917', 
                padding: '12px 24px',
                borderBottom: '1px solid #27272a',
                fontFamily: 'monospace',
                fontSize: '14px'
            }}>
                <span style={{ marginRight: '24px' }}>
                    <strong>user.role:</strong>{' '}
                    <span style={{ color: isAdmin ? '#22c55e' : '#ef4444' }}>
                        {user?.role || 'null'}
                    </span>
                </span>
                <span style={{ marginRight: '24px' }}>
                    <strong>systemMode:</strong>{' '}
                    <span style={{ color: isTesting ? '#22c55e' : '#ef4444' }}>
                        {systemMode}
                    </span>
                </span>
                <span>
                    <strong>hasAccess:</strong>{' '}
                    <span style={{ color: hasAccess ? '#22c55e' : '#ef4444' }}>
                        {String(hasAccess)}
                    </span>
                </span>
            </div>

            {/* Main content area */}
            <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
                {/* Access denied message - inside content, not blocking render */}
                {!hasAccess && (
                    <Card className="bg-zinc-900 border-zinc-800 mb-6">
                        <CardContent className="p-8 text-center">
                            <AlertTriangle className="w-12 h-12 text-orange-500 mx-auto mb-4" />
                            <h2 className="text-xl font-bold text-white mb-2">Access Restricted</h2>
                            <p className="text-zinc-400 mb-4">
                                Debug Console requires <strong>admin</strong> role and <strong>testing</strong> mode.
                            </p>
                            <p className="text-zinc-500 text-sm">
                                Current: role={user?.role || 'null'}, mode={systemMode}
                            </p>
                            <Button 
                                onClick={() => navigate('/settings')} 
                                variant="outline"
                                className="mt-4"
                            >
                                Go to Settings
                            </Button>
                        </CardContent>
                    </Card>
                )}

                {/* Debug content - only when has access */}
                {hasAccess && (
                    <>
                        {/* Error State */}
                        {error && (
                            <Card className="bg-red-950/50 border-red-900 mb-4">
                                <CardContent className="p-4">
                                    <div className="flex items-center gap-2 text-red-400">
                                        <AlertTriangle className="w-5 h-5" />
                                        <span>{error}</span>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Loading State */}
                        {isLoading && !debugData && (
                            <div className="flex items-center justify-center h-64">
                                <RefreshCw className="w-8 h-8 text-zinc-500 animate-spin" />
                            </div>
                        )}

                        {/* No Data - prompt to fetch */}
                        {!isLoading && !debugData && !error && (
                            <Card className="bg-zinc-900 border-zinc-800">
                                <CardContent className="p-12 text-center">
                                    <FileJson className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
                                    <h2 className="text-xl font-semibold text-white mb-2">No Debug Data Loaded</h2>
                                    <p className="text-zinc-400 mb-4">
                                        Click Refresh to load the last captured API request.
                                    </p>
                                    <Button onClick={fetchDebugData} className="bg-orange-600 hover:bg-orange-700">
                                        <RefreshCw className="w-4 h-4 mr-2" />
                                        Load Debug Data
                                    </Button>
                                </CardContent>
                            </Card>
                        )}

                        {/* Debug Data Display */}
                        {lastRequest && (
                            <div className="space-y-4">
                                {/* Request Summary */}
                                <Card className="bg-zinc-900 border-zinc-800">
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-lg text-white flex items-center gap-2">
                                            <FileJson className="w-5 h-5 text-orange-500" />
                                            Last API Request
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-xs font-semibold text-zinc-500 uppercase block mb-1">Endpoint</label>
                                                <code className="text-lg text-white font-mono bg-zinc-800 px-3 py-2 rounded block break-all">
                                                    {lastRequest.endpoint || 'N/A'}
                                                </code>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="text-xs font-semibold text-zinc-500 uppercase block mb-1">Method</label>
                                                    <Badge variant="outline" className="text-orange-400 border-orange-400/30 text-base px-3 py-1">
                                                        {lastRequest.method || 'N/A'}
                                                    </Badge>
                                                </div>
                                                <div>
                                                    <label className="text-xs font-semibold text-zinc-500 uppercase block mb-1">Status</label>
                                                    <Badge className={`${getStatusColor(lastRequest.status_code)} text-white text-base px-3 py-1`}>
                                                        {lastRequest.status_code || 'N/A'}
                                                    </Badge>
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-zinc-500 uppercase block mb-1">Timestamp</label>
                                            <div className="flex items-center gap-2 text-zinc-300">
                                                <Clock className="w-4 h-4 text-zinc-500" />
                                                <span className="font-mono">
                                                    {lastRequest.timestamp ? new Date(lastRequest.timestamp).toLocaleString() : 'N/A'}
                                                </span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Request Payload */}
                                <Card className="bg-zinc-900 border-zinc-800">
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-lg text-white">Request Payload</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <pre className="bg-zinc-950 border border-zinc-800 rounded-lg p-4 overflow-x-auto text-sm font-mono text-zinc-300 whitespace-pre-wrap break-words max-h-96 overflow-y-auto">
                                            {lastRequest.payload ? formatJson(lastRequest.payload) : 'No payload'}
                                        </pre>
                                    </CardContent>
                                </Card>

                                {/* Response Body */}
                                <Card className="bg-zinc-900 border-zinc-800">
                                    <CardHeader className="pb-3">
                                        <div className="flex items-center justify-between">
                                            <CardTitle className="text-lg text-white">Response Body</CardTitle>
                                            <Badge variant="outline" className={lastRequest.is_error ? "text-red-400 border-red-400/30" : "text-green-400 border-green-400/30"}>
                                                {lastRequest.is_error ? 'Error' : 'Success'}
                                            </Badge>
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        <pre className={`bg-zinc-950 border rounded-lg p-4 overflow-x-auto text-sm font-mono whitespace-pre-wrap break-words max-h-[500px] overflow-y-auto ${lastRequest.is_error ? 'border-red-900/50 text-red-300' : 'border-zinc-800 text-zinc-300'}`}>
                                            {lastRequest.response_body ? formatJson(lastRequest.response_body) : 'No response body'}
                                        </pre>
                                    </CardContent>
                                </Card>

                                {/* Recent Logs */}
                                <Card className="bg-zinc-900 border-zinc-800">
                                    <CardHeader className="pb-3">
                                        <div className="flex items-center justify-between">
                                            <CardTitle className="text-lg text-white">Recent Logs</CardTitle>
                                            <Badge variant="outline" className="text-zinc-500">{recentLogs.length} entries</Badge>
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        {recentLogs.length === 0 ? (
                                            <p className="text-zinc-500 text-center py-8">No recent logs</p>
                                        ) : (
                                            <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4 max-h-80 overflow-y-auto space-y-2">
                                                {recentLogs.map((log, i) => (
                                                    <div key={i} className="flex items-start gap-3 text-sm font-mono py-1 border-b border-zinc-800/50 last:border-0">
                                                        <Badge variant="outline" className={`flex-shrink-0 text-xs ${log.level === 'ERROR' ? 'text-red-400 border-red-400/30' : 'text-zinc-500 border-zinc-700'}`}>
                                                            {log.level}
                                                        </Badge>
                                                        <span className="text-zinc-500 flex-shrink-0 text-xs">
                                                            {new Date(log.timestamp).toLocaleTimeString()}
                                                        </span>
                                                        <span className="text-zinc-300 break-all">{log.message}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </div>
                        )}
                    </>
                )}

                {/* Footer */}
                <div className="text-center text-xs text-zinc-600 py-8">
                    Debug Console v1.0 | User: {user?.email || 'N/A'} | Mode: {systemMode}
                </div>
            </div>
        </div>
    );
}
